<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, onBeforeMount } from 'vue';
import Menu from 'primevue/menu';
import download from 'downloadjs';
import Papa from 'papaparse';
import CancelWorkflowDialog from './CancelWorkflowDialog.vue';
import MTSSReferralTable from './MTSSReferralTable.vue';
import InitialEvalReferralTable from './InitialEvalReferralTable.vue';
import TransferReferralTable from './TransferReferralTable.vue';
import ReassignmentReferralTable from './ReassignmentReferralTable.vue';
import ProcessReferralDialog from './ProcessReferralDialog.vue';
import UserReferralSISModal from './UserReferralSISModal.vue';
import Dialog from 'primevue/dialog';
import Accordian from 'primevue/accordion';
import AccordianTab from 'primevue/accordiontab';
import { EMIT, PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { DisciplineStateInterface } from '@/types';
import { StudentService } from '@/services';
import {
  useStudentReferralStore,
  useOrganizationStore,
  useClinicianOrganizationStore,
  useServicesStore,
  useDisciplineStore,
  useBuildingStore,
} from '@/store';
import { can, usePersonTypes, useRequestCancel, useFlattenOrganizations, useTenant } from '@/composables';
import { TreeNode } from 'primevue/tree';

const $tenant = useTenant();

const emit = defineEmits<{
  (e: EMIT.CANCEL): void;
  (e: EMIT.SAVE, id: number): void;
}>();

const modalVisible = ref(true);
const shouldProcessReferrals = ref<boolean>(false);
const organizationFilter = ref<number>();
const selectedOrganization = ref();
const openProcess = ref<boolean>(false);
const openCancel = ref<boolean>(false);
const openSISModal = ref<boolean>(false);
const organizations = ref<TreeNode[]>([]);
const bulkFile = ref();

const importMenu = ref();
const importMenuItems = ref<Array<{ label: string; icon: string; command: () => void }>>([]);

const referralStore = useStudentReferralStore();
const servicesStore = useServicesStore();
const organizationStore = useOrganizationStore();
const clinicianOrganizationStore = useClinicianOrganizationStore();
const disciplineStore = useDisciplineStore();
const requestCancel = useRequestCancel();

const buildingStore = useBuildingStore();
const disciplines = ref<DisciplineStateInterface[]>([]);

const openProcessModal = () => (openProcess.value = true);
const closeProcessModal = () => (openProcess.value = false);
const openCancelModal = () => (openCancel.value = true);
const closeCancelModal = () => (openCancel.value = false);

const cancel = () => {
  emit(EMIT.CANCEL);
  modalVisible.value = false;
  return;
};

const onCancel = () => {
  closeCancelModal();
  cancel();
};

const onImportMenu = (event) => importMenu.value.toggle(event);

const onSISModalCancel = () => (openSISModal.value = false);

onBeforeMount(async () => {
  window.addEventListener('beforeunload', onLeaving);
  await referralStore.loadOptions();
});

onMounted(() => {
  if (can([PERMISSIONS.REFERRAL_PROCESS])) init();
});

onBeforeUnmount(() => {
  toggleGesture(false);
  requestCancel.requestSaverAbortAll();
  window.removeEventListener('beforeunload', onLeaving);
});

//turn off/ on the gestures for mac
const toggleGesture = (stopGesture: boolean) => {
  document.body.style = stopGesture ? 'overscroll-behavior-x: none !important' : 'auto';
};

const init = async () => {
  toggleGesture(true);
  buildingStore.organizationBuildings = [];
  await disciplineStore.getDisciplines();
  await organizationStore.getOrganizations();
  organizations.value = useFlattenOrganizations(organizationStore.organizations.data);
  disciplines.value = disciplineStore.disciplines;
  if (usePersonTypes.isSchoolAdmin()) {
    organizationFilter.value = organizations.value.length === 1 ? organizations.value[0].id : undefined;

    if (organizations.value.length === 1) {
      await onOrganizationSelect();
    }
  }
};

const onLeaving = (event) => event.preventDefault();

const doDownload = async () => {
  const response = await StudentService.getStudentReferralTemplate();
  const content = response.headers['content-type'];

  download(response.data, 'student-referral-template.csv', content);
};

const saveAllProcessing = () => {
  closeProcessModal();
  shouldProcessReferrals.value = true;
};

const onOrganizationSelect = async () => {
  const organization = organizations.value.find((item) => item.id === organizationFilter.value);
  selectedOrganization.value = organization;
  defineImportMenuItems(organization);
  referralStore.resetReferralData();
  await clinicianOrganizationStore.getCliniciansForOrganizationRequest(organizationFilter.value as number);
  await servicesStore.getServices(organizationFilter.value as number);
  await disciplineStore.getFilterDisciplines(organizationFilter.value as number);
  await buildingStore.fetchOrganizationBuildings(organizationFilter.value as number);
  buildingStore.organizationBuildings.sort((a, b) => a.name.localeCompare(b.name));
};

const defineImportMenuItems = (organization) => {
  importMenuItems.value = [];
  importMenuItems.value.push({
    label: 'CSV File',
    icon: 'm-icon insert_drive_file',
    command: () => bulkFile.value.click(),
  });

  if (
    $tenant.tenantSettingEnabled(TENANT_SETTINGS.USE_INTEGRATION_SIS_SCHOOLDAY_ONEROSTER) &&
    organization.integration_organization
  ) {
    importMenuItems.value.push({
      label: 'School Day (SIS)',
      icon: 'm-icon storage',
      command: () => (openSISModal.value = true),
    });
  }
};

const tableSaved = () => (shouldProcessReferrals.value = false);

const onBulkFileUpload = () => {
  Papa.parse(bulkFile.value.files[0], {
    header: true,
    complete: function (results) {
      results.data.forEach((row) => {
        if (row.building) {
          const normalizedCSVBuildingName = row.building.trim().toLowerCase();
          const foundBuilding = buildingStore.organizationBuildings.find(
            (b) => b.name.trim().toLowerCase() === normalizedCSVBuildingName,
          );
          if (foundBuilding) {
            row.building_id = foundBuilding.id;
            row.building_name = foundBuilding.name;
          }
        }

        if (row.referred_discipline) {
          const normalizedCSVDisciplineName = row.referred_discipline.trim().toLowerCase();
          const foundDiscipline = disciplines.value.find(
            (b) => b.name.trim().toLowerCase() === normalizedCSVDisciplineName,
          );
          if (foundDiscipline) {
            row.discipline_id = foundDiscipline.id;
            row.discipline_name = foundDiscipline.name;
          }
        }
      });
      referralStore.onBulkFileImport(results.data);
    },
  });
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    class="workflow__dialog__container bg-white border-noround p-dialog-maximized"
    :modal="true"
    :closable="false"
    :close-on-escape="false"
    @hide="cancel"
  >
    <template #header>
      <h2 class="px-3" v-text="'Referral Management Workflow'" />
    </template>
    <template #default>
      <ProcessReferralDialog :open="openProcess" @cancel="closeProcessModal" @update="saveAllProcessing" />
      <CancelWorkflowDialog :open="openCancel" @cancel="closeCancelModal" @update="onCancel" />
      <UserReferralSISModal
        v-if="selectedOrganization?.integration_organization"
        :organization="selectedOrganization"
        :open="openSISModal"
        @cancel="onSISModalCancel"
      />
      <div class="flex flex-wrap gap-3 my-3 px-4">
        <div class="flex align-items-center gap-3 flex-1">
          <Skeleton v-if="!organizations.length" height="36px" width="160px" border-radius="4" />
          <Dropdown
            v-else
            id="organizations"
            v-model="organizationFilter"
            type="secondary"
            :options="organizations"
            filter
            option-label="name"
            option-value="id"
            placeholder="Select Organization"
            @change="onOrganizationSelect"
          />
          <AppButton
            icon-left="swap_vert"
            type="secondary"
            text="Import"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            :disabled="!organizationFilter"
            @click="onImportMenu"
          />
          <Menu id="overlay_menu" ref="importMenu" :model="importMenuItems" :popup="true" />
          <input ref="bulkFile" type="file" class="hidden" @change="onBulkFileUpload" />
          <div class="flex text-primary cursor-pointer" @click.stop.prevent="doDownload">
            <Icon name="insert_drive_file" />
            <span class="underline mt-1 ml-2 text-sm">Download Template</span>
          </div>
          <div class="ml-auto inline-flex gap-3">
            <AppButton id="cancel_service_btn" type="secondary" text="Cancel / Close" @click="openCancelModal" />
            <AppButton
              id="save_service_btn"
              type="primary"
              text="Process Referrals"
              :disabled="!organizationFilter"
              @click="openProcessModal"
            />
          </div>
        </div>
      </div>
      <template v-if="!referralStore.referralOptionsloading">
        <Accordian :multiple="true" :active-index="[0, 1, 2, 3]">
          <AccordianTab>
            <template #header>
              <span class="flex align-items-center gap-3 w-full accordian__header">
                <span class="font-bold white-space-nowrap header__text">MTSS Referral</span>
                <div class="row__count__container flex align-items-center justify-content-center">
                  {{ referralStore.mTSSReferralTableData.length }}
                </div>
              </span>
            </template>
            <template #default>
              <MTSSReferralTable
                :save="shouldProcessReferrals"
                :organization-id="organizationFilter"
                :buildings="buildingStore.organizationBuildings"
                @save="tableSaved"
              />
            </template>
          </AccordianTab>
          <AccordianTab>
            <template #header>
              <span class="flex align-items-center gap-3 w-full accordian__header">
                <span class="font-bold white-space-nowrap header__text">Initial Evaluation/Referral</span>
                <div class="row__count__container flex align-items-center justify-content-center">
                  {{ referralStore.initialEvalReferralTableData.length }}
                </div>
              </span>
            </template>
            <template #default>
              <InitialEvalReferralTable
                :save="shouldProcessReferrals"
                :organization-id="organizationFilter"
                :buildings="buildingStore.organizationBuildings"
                :disciplines="disciplines"
                :services="disciplineStore.disciplines"
                @save="tableSaved"
              />
            </template>
          </AccordianTab>
          <AccordianTab>
            <template #header>
              <span class="flex align-items-center gap-3 w-full accordian__header">
                <span class="font-bold white-space-nowrap header__text">Transfer</span>
                <div class="row__count__container flex align-items-center justify-content-center">
                  {{ referralStore.transferReferralTableData.length }}
                </div>
              </span>
            </template>
            <template #default>
              <TransferReferralTable
                :save="shouldProcessReferrals"
                :organization-id="organizationFilter"
                :buildings="buildingStore.organizationBuildings"
                :disciplines="disciplines"
                :services="disciplineStore.disciplines"
                @save="tableSaved"
              />
            </template>
          </AccordianTab>
          <AccordianTab>
            <template #header>
              <span class="flex align-items-center gap-3 w-full accordian__header">
                <span class="font-bold white-space-nowrap header__text">Reassignment</span>
                <div class="row__count__container flex align-items-center justify-content-center">
                  {{ referralStore.reassignmentReferralTableData.length }}
                </div>
              </span>
            </template>
            <template #default>
              <ReassignmentReferralTable
                :save="shouldProcessReferrals"
                :organization-id="organizationFilter"
                :buildings="buildingStore.organizationBuildings"
                :disciplines="disciplines"
                :services="disciplineStore.disciplines"
                @save="tableSaved"
              />
            </template>
          </AccordianTab>
        </Accordian>
      </template>
      <template v-else>
        <h2 class="p-8 w-full text-center text-3xl text-700" v-text="'Preparing Interface'" />
      </template>
    </template>
  </Dialog>
</template>

<style lang="scss">
html,
body {
  overscroll-behavior-x: none !important;
}

.workflow__dialog__container {
  .p-dialog-header {
    padding: 12px !important;
    background: var(--surface-ground) !important;
    align-items: center;
  }

  .p-dialog-content {
    background: white !important;
    margin-top: 0;
    padding: 0 !important;
  }
  .p-accordion {
    border-radius: 0 !important;
    .p-accordion-header {
      background-color: var(--primary-color-extra-light);
      &:hover {
        background-color: var(--primary-color-extra-light);
      }
      .p-accordion-header-link {
        background: transparent !important;
        text-decoration: none;
        &:hover {
          background: transparent !important;
        }
        .p-accordion-toggle-icon {
          color: var(--primary-color);
          margin-right: 1rem !important;
          margin-left: 0.5rem !important;
          font-size: 11px;
        }
      }
      .accordian__header {
        .header__text {
          color: var(--primary-color);
          font-size: 18px;
        }
        .row__count__container {
          width: 25px;
          height: 25px;
          border-radius: 6px;
          border: 2px solid var(--primary-color-light);
          background-color: var(--primary-color-extra-light);
          color: var(--primary-color);
        }
      }
    }
  }
  .p-accordion-content {
    padding: 10px 5px 10px 15px;
  }
  .add__row__icon {
    color: var(--primary-color);
  }
  .remove__row__icon {
    color: $red;
  }
}

.workflow__dialog__container td.p-frozen-column {
  z-index: 1 !important;
}

.workflow__dialog__container .p-datepicker {
  z-index: 0 !important;
}

.workflow__dialog__container .p-dropdown-panel {
  z-index: 0 !important;
}
</style>
